// NOTE: hooks
import { lazy, ReactNode, Suspense } from 'react'
import { Navigate, Outlet } from 'react-router-dom'
import usePermissionSync from '../hooks/usePermissionSync'
// NOTE: components
import Loading from '../components/ui/Loading'
import Layout from '../components/ui/Layout'
import SubPage from '../components/ui/SubPage'
// NOTE: images
import {
  PiHouseFill as HomeIcon,
  PiHouse as HomeOutlineIcon
} from 'react-icons/pi'
import {
  IoPeople as PatientsIcon,
  IoPeopleOutline as PatientsOutlineIcon,
  IoSettings as SettingsIcon,
  IoSettingsOutline as SettingsOutlineIcon
} from 'react-icons/io5'
import {
  MdLibraryAdd as LibraryIcon,
  MdOutlineLibraryAdd as LibraryOutlineIcon
} from 'react-icons/md'
import {
  BsExclamationCircleFill as ActionsIcon,
  BsExclamationCircle as ActionsOutlineIcon
} from 'react-icons/bs'
// NOTE: home pages
const Home = lazy(() => import('./home/Home'))
// NOTE: actions pages
const Actions = lazy(() => import('./actions/Actions'))
const Investigations = lazy(() => import('./actions/investigations/Investigations'))
const LifestyleAssessments = lazy(() => import('./actions/lifestyleAssessments/LifestyleAssessments'))
const FormResponses = lazy(() => import('./actions/formResponses/FormResponses'))
const VitalsAssessments = lazy(() => import('./actions/vitals/Vitals'))
const WheelOfLife = lazy(() => import('./actions/wheelOfLife/WheelOfLife'))
const NewPatients = lazy(() => import('./actions/newPatients/NewPatients'))
const UnassignedPatients = lazy(() => import('./actions/unassignedPatients/UnassignedPatients'))
// NOTE: patient page
const Patient = lazy(() => import('./patient/Patient'))
// NOTE: patient lifestyle tool pages
const Tdee = lazy(() => import('./lifestyleTools/tdee/Tdee'))
const WaterNeed = lazy(() => import('./lifestyleTools/water/WaterNeed'))
const Breathing = lazy(() => import('./lifestyleTools/breathing/Breathing'))
const Life = lazy(() => import('./lifestyleTools/life/Life'))
const Lifestyle = lazy(() => import('./lifestyleTools/lifestyle/Lifestyle'))
const Vitals = lazy(() => import('./lifestyleTools/vitals/Vitals'))
// NOTE: content pages
const ContentItem = lazy(() => import('./contentItem/ContentItem'))
// NOTE: settings pages
const Personal = lazy(() => import('./personal/Personal'))
const Staff = lazy(() => import('./staff/Staff'))
const Partners = lazy(() => import('./partners/Partners'))
const Website = lazy(() => import('./website/Website'))
const Clinic = lazy(() => import('./clinic/Clinic'))
const Help = lazy(() => import('./help/Help'))
const Setup = lazy(() => import('./setup/Setup'))
// NOTE: library pages
const Featured = lazy(() => import('./featured/Featured'))
const Service = lazy(() => import('./service/Service'))
const Forms = lazy(() => import('./forms/Forms'))
const Content = lazy(() => import('./content/Content'))
const Tests = lazy(() => import('./tests/Tests'))

const navLinks = [
  {
    to: '/home/',
    text: 'Home',
    activeIcon: <HomeIcon className="mx-auto h-auto w-[34px] lg:mx-0 lg:w-[30px]" />,
    inactiveIcon: <HomeOutlineIcon className="mx-auto h-auto w-[34px] lg:mx-0 lg:w-[30px]" />
  },
  {
    to: '/actions/',
    text: 'Actions',
    activeIcon: <ActionsIcon className="mx-auto h-auto w-[34px] lg:mx-0 lg:w-[30px]" />,
    inactiveIcon: <ActionsOutlineIcon className="mx-auto h-auto w-[34px] lg:mx-0 lg:w-[30px]" />
  },
  {
    to: '/patients/',
    text: 'Patients',
    activeIcon: <PatientsIcon className="mx-auto h-auto w-[34px] lg:mx-0 lg:w-[30px]" />,
    inactiveIcon: <PatientsOutlineIcon className="mx-auto h-auto w-[34px] lg:mx-0 lg:w-[30px]" />
  },
  {
    to: '/library/',
    text: 'Library',
    activeIcon: <LibraryIcon className="mx-auto h-auto w-[34px] lg:mx-0 lg:w-[30px]" />,
    inactiveIcon: <LibraryOutlineIcon className="mx-auto h-auto w-[34px] lg:mx-0 lg:w-[30px]" />
  },
  {
    to: '/settings/',
    text: 'Settings',
    activeIcon: <SettingsIcon className="mx-auto h-auto w-[34px] lg:mx-0 lg:w-[30px]" />,
    inactiveIcon: <SettingsOutlineIcon className="mx-auto h-auto w-[34px] lg:mx-0 lg:w-[30px]" />
  }
]

const settingsLinks = [
  {
    to: 'personal',
    text: 'Personal'
  },
  {
    to: 'clinic',
    text: 'Clinic'
  },
  {
    to: 'staff',
    text: 'Staff'
  },
  {
    to: 'partners',
    text: 'Partners'
  },
  {
    to: 'website',
    text: 'Website'
  },
  {
    to: 'setup',
    text: 'Setup'
  },
  {
    to: 'help',
    text: 'Help'
  }
]

const libraryLinks = [
  {
    to: 'featured',
    text: 'Featured'
  },
  {
    to: 'content',
    text: 'Content'
  },
  {
    to: 'services',
    text: 'Services'
  },
  {
    to: 'forms',
    text: 'Forms'
  },
  {
    to: 'tests',
    text: 'Tests'
  }
]

interface LoadPageProps {
  page: ReactNode
}

// NOTE: component to show loading animation
const LoadPage = ({ page }: LoadPageProps) => (
  <Suspense fallback={<Loading size={undefined} />}>{page}</Suspense>
)

const ProtectedClinician = () => {
  // NOTE: Sync user permissions in real-time
  usePermissionSync()

  return <Outlet />
}

const clinicianRoutes = [
  {
    element: <ProtectedClinician />,
    children: [
      {
        element: <Layout links={navLinks} home='/' />,
        children: [
          {
            index: true,
            element: <Navigate to='home/' />
          },
          {
            path: 'home',
            element: <LoadPage page={<Home />} />
          },
          {
            path: 'patients',
            element: <LoadPage page={<Patient />} />,
          },
          {
            path: 'actions',
            children: [
              {
                index: true,
                element: <LoadPage page={<Actions />} />
              },
              {
                path: 'investigations',
                element: <LoadPage page={<Investigations />} />
              },
              {
                path: 'lifestyle-assessments',
                element: <LoadPage page={<LifestyleAssessments />} />
              },
              {
                path: 'forms',
                element: <LoadPage page={<FormResponses />} />
              },
              {
                path: 'vitals',
                element: <LoadPage page={<VitalsAssessments />} />
              },
              {
                path: 'wheel-of-life',
                element: <LoadPage page={<WheelOfLife />} />
              },
              {
                path: 'new-patients',
                element: <LoadPage page={<NewPatients />} />
              },
              {
                path: 'unassigned-patients',
                element: <LoadPage page={<UnassignedPatients />} />
              }
            ]
          },
          {
            path: 'patients/:patientId/lifestyle/energy',
            element: <LoadPage page={<Tdee />} />
          },
          {
            path: 'patients/:patientId/lifestyle/water',
            element: <LoadPage page={<WaterNeed />} />
          },
          {
            path: 'patients/:patientId/lifestyle/breathing',
            element: <LoadPage page={<Breathing />} />
          },
          {
            path: 'patients/:patientId/lifestyle/life',
            element: <LoadPage page={<Life />} />
          },
          {
            path: 'patients/:patientId/lifestyle/lifestyle',
            element: <LoadPage page={<Lifestyle />} />
          },
          {
            path: 'patients/:patientId/lifestyle/vitals',
            element: <LoadPage page={<Vitals />} />
          },
          {
            path: 'contents/:contentId',
            element: <LoadPage page={<ContentItem />} />
          },
          {
            path: 'settings/help/:contentId',
            element: <LoadPage page={<ContentItem isHelp />} />
          },
          {
            path: 'library',
            element: <SubPage title="Library" links={libraryLinks} />,
            children: [
              {
                index: true,
                element: <Navigate to="featured" />
              },
              {
                path: 'featured',
                element: <LoadPage page={<Featured />} />
              },
              {
                path: 'services',
                element: <LoadPage page={<Service />} />
              },
              {
                path: 'forms',
                element: <LoadPage page={<Forms />} />
              },
              {
                path: 'content',
                element: <LoadPage page={<Content />} />
              },
              {
                path: 'tests',
                element: <LoadPage page={<Tests />} />
              }
            ]
          },
          {
            path: 'settings',
            element: <SubPage title="Settings" links={settingsLinks} />,
            children: [
              {
                index: true,
                element: <Navigate to="personal" />
              },
              {
                path: 'personal',
                element: <LoadPage page={<Personal />} />
              },
              {
                path: 'clinic',
                element: <LoadPage page={<Clinic />} />
              },
              {
                path: 'staff',
                element: <LoadPage page={<Staff />} />
              },
              {
                path: 'partners',
                element: <LoadPage page={<Partners />} />
              },
              {
                path: 'website',
                element: <LoadPage page={<Website />} />
              },
              {
                path: 'setup',
                element: <LoadPage page={<Setup />} />
              },
              {
                path: 'help',
                element: <LoadPage page={<Help />} />
              }
            ]
          }
        ]
      }
    ]
  }
]

export default clinicianRoutes
